// Dynamic GraphQL Client with automatic API key rotation
import { mockGraphQLClient } from './mockClient';

class DynamicGraphQLClient {
  constructor() {
    this.apiKey = null;
    this.endpoint = null;
    this.keyExpiry = null;
    this.lastFetch = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    
    // Configuration endpoint for fetching API keys dynamically
    this.configEndpoint = 'https://fqmxnc6w7f6hlygenh2v3tpt4u0sovkm.lambda-url.ap-southeast-2.on.aws/';
    this.keyRefreshInterval = 24 * 60 * 60 * 1000; // 24 hours
    this.keyExpiryBuffer = 7 * 24 * 60 * 60 * 1000; // 7 days before expiry
    
    // Use mock client for local development
    this.useMock = process.env.NODE_ENV === 'development' || 
                   window.location.hostname === 'localhost' ||
                   window.location.hostname === '127.0.0.1';
    
    // Debug logging
    console.log('🔍 DynamicGraphQLClient Debug Info:');
    console.log('- NODE_ENV:', process.env.NODE_ENV);
    console.log('- hostname:', window.location.hostname);
    console.log('- useMock:', this.useMock);
    
    if (this.useMock) {
      console.log('🔧 Using mock GraphQL client for local development');
    } else {
      console.log('🔑 Production mode - will fetch API configuration dynamically from Lambda service');
    }
  }

  async getApiConfig() {
    // Use mock client for local development
    if (this.useMock) {
      return {
        apiKey: 'mock-key',
        endpoint: 'mock-endpoint'
      };
    }

    // Check if we need to refresh the configuration
    const now = Date.now();
    const shouldRefresh = !this.apiKey || 
                         !this.lastFetch || 
                         (now - this.lastFetch) > this.keyRefreshInterval ||
                         (this.keyExpiry && (this.keyExpiry - now) < this.keyExpiryBuffer);

    if (shouldRefresh && !this.isRefreshing) {
      this.isRefreshing = true;
      
      try {
        console.log('🔄 Fetching fresh API configuration from Lambda service...');
        
        const response = await fetch(this.configEndpoint, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch API config: ${response.status} ${response.statusText}`);
        }

        const configData = await response.json();
        
        // Update our cached configuration
        this.apiKey = configData.apiKey;
        this.endpoint = configData.endpoint;
        this.lastFetch = now;
        
        // Calculate expiry timestamp if provided
        if (configData.daysUntilExpiry) {
          this.keyExpiry = now + (configData.daysUntilExpiry * 24 * 60 * 60 * 1000);
        }
        
        console.log(`✅ API configuration updated successfully (expires in ${configData.daysUntilExpiry} days)`);
        
      } catch (error) {
        console.warn('⚠️ Failed to fetch fresh API config, using cached values:', error.message);
        
        // Fall back to hardcoded values if fetch fails
        if (!this.apiKey) {
          this.apiKey = 'da2-nsdyiecjlffg5cuhompdtymmky';
          this.endpoint = 'https://k4pfwywhcjgytgsitr735nso5i.appsync-api.ap-southeast-2.amazonaws.com/graphql';
          console.log('🔑 Using fallback API configuration');
        }
      } finally {
        this.isRefreshing = false;
      }
    }

    return {
      apiKey: this.apiKey,
      endpoint: this.endpoint
    };
  }

  async query(query, variables = {}) {
    // Use mock client for local development
    if (this.useMock) {
      console.log('🔧 Using mock client for query:', query.includes('getTags') ? 'getTags' : 'other');
      return mockGraphQLClient.query(query, variables);
    }

    const config = await this.getApiConfig();
    
    try {
      console.log('🌐 Making GraphQL request:', query.includes('getTags') ? 'getTags' : 'other');
      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey,
        },
        body: JSON.stringify({
          query,
          variables,
        }),
      });

      if (response.status === 401) {
        console.warn('🔑 Received 401 Unauthorized - API key may have expired');
        throw new Error('GraphQL API key expired or invalid');
      }

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Debug logging for getTags specifically
      if (query.includes('getTags')) {
        console.log('✅ getTags response:', result.data.getTags ? `${result.data.getTags.length} tags` : 'no tags');
      }

      return result.data;
      
    } catch (error) {
      console.error('GraphQL query error:', error);
      throw error;
    }
  }

  // Health check method
  async healthCheck() {
    try {
      const config = await this.getApiConfig();
      
      // Simple query to test connectivity
      await this.query('query { getTags }');
      
      return {
        status: 'healthy',
        apiKey: config.apiKey ? `${config.apiKey.substring(0, 8)}...` : 'none',
        endpoint: config.endpoint,
        lastFetch: this.lastFetch ? new Date(this.lastFetch).toISOString() : 'never'
      };
      
    } catch (error) {
      return {
        status: 'unhealthy',
        error: error.message,
        lastFetch: this.lastFetch ? new Date(this.lastFetch).toISOString() : 'never'
      };
    }
  }

  // Manual refresh method for debugging
  async forceRefresh() {
    console.log('🔄 Manual refresh requested - fetching from Lambda service...');
    this.lastFetch = null; // Force refresh
    return this.getApiConfig();
  }
}

// Create and export singleton instance
export const dynamicGraphQLClient = new DynamicGraphQLClient();

// Export class for testing
export default DynamicGraphQLClient;
